import React, { useState, useEffect, useRef } from 'react';
import GenericModal from '../../components/agv-generic-modal';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../enums/modal-size.enum';
import FormInput from '../../components/agv-forms-input';
import usePostFactory from '../../api/factories/usePostFactory';
import axios from 'axios';

export default function CreateWarehouseModal({ isOpen, setIsOpen, onSuccess }) {
  const [warehouseData, setWarehouseData] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    latitude: '',
    longitude: '',
    external_id: '',
  });

  const [addressQuery, setAddressQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  const { isLoading, postFactory } = usePostFactory();

  // Debounced geocoding search using Nominatim (OpenStreetMap)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (addressQuery.length > 2) {
      setIsSearching(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await axios.get(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}&countrycodes=fr&limit=5`,
            {
              headers: {
                'Accept-Language': 'fr',
              },
            }
          );
          setSearchResults(response.data);
        } catch (error) {
          console.error('Erreur lors de la recherche géographique:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 500);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [addressQuery]);

  const handleClose = () => {
    setIsOpen(false);
    setWarehouseData({
      name: '',
      address: '',
      city: '',
      postal_code: '',
      latitude: '',
      longitude: '',
      external_id: '',
    });
    setAddressQuery('');
    setSearchResults([]);
  };

  const handleSelectAddress = (result) => {
    // Parse address components from display_name
    const addressParts = result.display_name.split(', ');
    
    setWarehouseData({
      ...warehouseData,
      address: addressParts[0] || '',
      city: addressParts[1] || '',
      postal_code: addressParts.find(part => /^\d{5}$/.test(part)) || '',
      latitude: result.lat,
      longitude: result.lon,
    });
    setAddressQuery(''); // Vider le champ de recherche
    setSearchResults([]);
  };

  const handleSubmit = async () => {
    try {
      const formattedData = {
        name: warehouseData.name,
        street: warehouseData.address || null,
        city: warehouseData.city || null,
        zipcode: warehouseData.postal_code || null,
        latitude: warehouseData.latitude ? parseFloat(warehouseData.latitude) : null,
        longitude: warehouseData.longitude ? parseFloat(warehouseData.longitude) : null,
        external_id: warehouseData.external_id ? parseInt(warehouseData.external_id, 10) : null,
      };

      await postFactory(formattedData);
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Erreur lors de la création de l\'entrepôt:', error);
    }
  };

  const isFormValid = () => {
    return warehouseData.name.trim() !== '';
  };

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={handleClose}
      title="Créer un nouvel entrepôt"
      mainAction={handleSubmit}
      mainActionText="Créer"
      mainActionDisabled={!isFormValid()}
      mainActionIsLoading={isLoading}
      secondaryAction={handleClose}
      secondaryActionText="Annuler"
      size={MODAL_SIZE_STYLE[MODAL_SIZE.LG]}
    >
      <div className="tw-space-y-4">
        <div className="warehouse-geocoder-container" style={{ position: 'relative' }}>
          <FormInput
            fieldTitle="Rechercher une adresse"
            value={addressQuery}
            setValue={(value) => setAddressQuery(value)}
            placeholder="Tapez une adresse pour rechercher..."
          />
          
          {isSearching && (
            <div className="warehouse-geocoder-loading">
              <div className="warehouse-geocoder-spinner"></div>
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="warehouse-geocoder-results">
              {searchResults.map((result) => (
                <div
                  key={result.place_id}
                  className="warehouse-geocoder-result-item"
                  onClick={() => handleSelectAddress(result)}
                >
                  <div className="tw-font-medium tw-text-sm">
                    {result.display_name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <FormInput
          fieldTitle="Nom de l'entrepôt"
          value={warehouseData.name}
          setValue={(value) =>
            setWarehouseData({ ...warehouseData, name: value })
          }
          placeholder="Ex: Entrepôt Paris Nord"
          isRequired={true}
        />

        <FormInput
          fieldTitle="Code externe (optionnel)"
          value={warehouseData.external_id}
          setValue={(value) =>
            setWarehouseData({ ...warehouseData, external_id: value })
          }
          placeholder="Ex: 1001"
          type="number"
        />

        {warehouseData.address && (
          <div className="warehouse-address-display">
            <label className="warehouse-address-label">Adresse sélectionnée</label>
            <div className="warehouse-address-content">
              <div className="warehouse-address-line">
                <span className="warehouse-address-value">{warehouseData.address}</span>
              </div>
              <div className="warehouse-address-line">
                <span className="warehouse-address-value">
                  {warehouseData.postal_code && `${warehouseData.postal_code} `}
                  {warehouseData.city}
                </span>
              </div>
            </div>
          </div>
        )}

        {warehouseData.latitude && warehouseData.longitude && (
          <div className="warehouse-coordinates-display">
            <div className="warehouse-coordinate">
              <label>Latitude</label>
              <span>{parseFloat(warehouseData.latitude).toFixed(6)}</span>
            </div>
            <div className="warehouse-coordinate">
              <label>Longitude</label>
              <span>{parseFloat(warehouseData.longitude).toFixed(6)}</span>
            </div>
          </div>
        )}

        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
          <FormInput
            fieldTitle="Latitude (manuel)"
            value={warehouseData.latitude}
            setValue={(value) =>
              setWarehouseData({ ...warehouseData, latitude: value })
            }
            placeholder="Ex: 48.856614"
            type="number"
          />

          <FormInput
            fieldTitle="Longitude (manuel)"
            value={warehouseData.longitude}
            setValue={(value) =>
              setWarehouseData({ ...warehouseData, longitude: value })
            }
            placeholder="Ex: 2.352222"
            type="number"
          />
        </div>
      </div>
    </GenericModal>
  );
}

